/** @typedef {'urun'|'parca'|'islem'} IcerikEditTipi */
/** @typedef {{ type: 'kategori', kategoriId: string } | { type: 'urun', urunIcerikId: string }} IcerikEditScope */

export const TEMPLATE_VERSION = 'v1';
export const META_PREFIX = 'UNMAK_ICERIK_EDIT';

/** @type {Record<IcerikEditTipi, { headers: string[] }>} */
export const EDIT_TEMPLATES = {
  parca: {
    headers: ['_id', 'Parça Kodu', 'Parça Adı', 'Değişim Garantisi', 'Brüt Fiyat'],
  },
  urun: {
    headers: ['_id', 'Ürün Kodu', 'Ürün Adı', 'Açıklama'],
  },
  islem: {
    headers: ['_id', 'İşlem Kodu', 'İşlem Adı', 'Garanti Dahili Fiyat', 'Garanti Harici Fiyat', 'Açıklama'],
  },
};

const cellStr = (v) => {
  if (v === undefined || v === null) return '';
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return String(v).trim();
};

const headersMatch = (row, expected) => {
  if (!Array.isArray(row) || row.length !== expected.length) return false;
  return expected.every((h, i) => cellStr(row[i]) === h);
};

/**
 * @param {IcerikEditTipi} icerikTipi
 * @param {IcerikEditScope} scope
 */
export const buildMetaLine = (icerikTipi, scope) => {
  const scopeType = scope.type;
  const scopeId = scope.type === 'kategori' ? scope.kategoriId : scope.urunIcerikId;
  return `${META_PREFIX};${TEMPLATE_VERSION};${icerikTipi};${scopeType};${scopeId}`;
};

/**
 * @param {string} raw
 * @returns {{ ok: true, icerikTipi: IcerikEditTipi, scope: IcerikEditScope } | { ok: false, error: string }}
 */
export const parseMetaLine = (raw) => {
  const parts = cellStr(raw).split(';');
  if (parts.length < 5 || parts[0] !== META_PREFIX) {
    return { ok: false, error: 'Geçersiz şablon meta satırı (satır 1).' };
  }
  const version = parts[1];
  if (version !== TEMPLATE_VERSION) {
    return { ok: false, error: `Desteklenmeyen şablon sürümü: ${version || '(boş)'}` };
  }
  const icerikTipi = parts[2];
  if (!EDIT_TEMPLATES[icerikTipi]) {
    return { ok: false, error: `Geçersiz içerik tipi: ${icerikTipi}` };
  }
  const scopeType = parts[3];
  const scopeId = parts[4];
  if (!scopeId || !/^[a-fA-F0-9]{24}$/.test(scopeId)) {
    return { ok: false, error: 'Geçersiz kapsam ID (meta satırı).' };
  }
  if (scopeType === 'kategori') {
    return { ok: true, icerikTipi, scope: { type: 'kategori', kategoriId: scopeId } };
  }
  if (scopeType === 'urun') {
    return { ok: true, icerikTipi, scope: { type: 'urun', urunIcerikId: scopeId } };
  }
  return { ok: false, error: `Geçersiz kapsam tipi: ${scopeType}` };
};

const boolToExcel = (v) => (v === true ? 'Evet' : 'Hayır');

const getItemId = (item) => String(item?.id || item?._id || '').trim();

const getItemKod = (item) =>
  cellStr(
    item?.icerikKodu ||
      item?.urunKodu ||
      item?.parcaKodu ||
      item?.islemKodu ||
      item?.kod,
  );

const getItemAd = (item) =>
  cellStr(
    item?.icerikAdi ||
      item?.urunAdi ||
      item?.parcaAdi ||
      item?.islemAdi ||
      item?.ad,
  );

const getItemAciklama = (item) =>
  cellStr(item?.aciklama || item?.urunAciklamasi || item?.parcaAciklamasi || item?.islemAciklamasi);

/**
 * @param {IcerikEditTipi} icerikTipi
 * @param {object} item
 * @returns {string[]}
 */
export const contentToExportRow = (icerikTipi, item) => {
  const id = getItemId(item);
  if (icerikTipi === 'parca') {
    return [
      id,
      getItemKod(item),
      getItemAd(item),
      boolToExcel(item?.degisimParcaGarantisi === true),
      item?.fiyat != null && item?.fiyat !== '' ? String(item.fiyat) : '',
    ];
  }
  if (icerikTipi === 'urun') {
    return [id, getItemKod(item), getItemAd(item), getItemAciklama(item)];
  }
  return [
    id,
    getItemKod(item),
    getItemAd(item),
    item?.fiyatGarantiDahili != null && item?.fiyatGarantiDahili !== ''
      ? String(item.fiyatGarantiDahili)
      : item?.fiyatgaranti != null && item?.fiyatgaranti !== ''
        ? String(item.fiyatgaranti)
        : '',
    item?.fiyatGarantiHarici != null && item?.fiyatGarantiHarici !== ''
      ? String(item.fiyatGarantiHarici)
      : item?.fiyat != null && item?.fiyat !== ''
        ? String(item.fiyat)
        : '',
    getItemAciklama(item),
  ];
};

/**
 * @param {IcerikEditTipi} icerikTipi
 * @param {IcerikEditScope} scope
 * @param {object[]} items
 * @returns {unknown[][]}
 */
export const buildExportAoA = (icerikTipi, scope, items) => {
  const headers = EDIT_TEMPLATES[icerikTipi].headers;
  const meta = buildMetaLine(icerikTipi, scope);
  const dataRows = (items || []).map((item) => contentToExportRow(icerikTipi, item));
  return [[meta], headers, ...dataRows];
};

export const parseBoolTrExcel = (raw) => {
  const t = cellStr(raw).toLowerCase();
  if (t === '') return { ok: false, error: 'Değişim garantisi boş olamaz (Evet veya Hayır).' };
  if (['evet', 'e', '1', 'true', 'yes'].includes(t)) return { ok: true, value: true };
  if (['hayır', 'hayir', 'h', '0', 'false', 'no'].includes(t)) return { ok: true, value: false };
  return { ok: false, error: 'Değişim garantisi Evet veya Hayır olmalıdır.' };
};

const parseFiyat = (raw) => {
  const s = cellStr(raw).replace(/\s/g, '').replace(',', '.');
  if (s === '') return { ok: true, value: null };
  const n = Number(s);
  if (Number.isNaN(n) || n < 0) return { ok: false, error: 'Geçersiz brüt fiyat.' };
  return { ok: true, value: n };
};

const parseKod = (raw) => {
  const kod = cellStr(raw);
  if (!kod) return { ok: false, error: 'Kod boş olamaz.' };
  if (/\s/.test(kod)) return { ok: false, error: 'Kod boşluk içeremez.' };
  if (kod.length > 50) return { ok: false, error: 'Kod en fazla 50 karakter olabilir.' };
  return { ok: true, value: kod };
};

const parseAd = (raw) => {
  const ad = cellStr(raw);
  if (!ad) return { ok: false, error: 'Ad boş olamaz.' };
  if (ad.length > 200) return { ok: false, error: 'Ad en fazla 200 karakter olabilir.' };
  return { ok: true, value: ad };
};

const parseAciklama = (raw) => {
  const a = cellStr(raw);
  if (a.length > 2000) return { ok: false, error: 'Açıklama en fazla 2000 karakter olabilir.' };
  return { ok: true, value: a };
};

const isRowEmpty = (row) => {
  if (!Array.isArray(row)) return true;
  return row.every((c) => cellStr(c) === '');
};

/**
 * @param {IcerikEditTipi} expectedTipi
 * @param {IcerikEditScope} expectedScope
 * @param {unknown[][]} allRows
 */
export const validateAndParseEditWorkbook = (expectedTipi, expectedScope, allRows) => {
  if (!Array.isArray(allRows) || allRows.length < 2) {
    return { ok: false, error: 'Dosyada meta ve başlık satırları bulunamadı.' };
  }

  const metaParsed = parseMetaLine(allRows[0]?.[0]);
  if (!metaParsed.ok) return metaParsed;

  if (metaParsed.icerikTipi !== expectedTipi) {
    return {
      ok: false,
      error: `Dosya tipi uyuşmuyor: beklenen ${expectedTipi}, dosyada ${metaParsed.icerikTipi}.`,
    };
  }

  const expScopeId =
    expectedScope.type === 'kategori' ? expectedScope.kategoriId : expectedScope.urunIcerikId;
  const fileScopeId =
    metaParsed.scope.type === 'kategori'
      ? metaParsed.scope.kategoriId
      : metaParsed.scope.urunIcerikId;
  if (metaParsed.scope.type !== expectedScope.type || fileScopeId !== expScopeId) {
    return { ok: false, error: 'Dosya bu kategori veya ürün kapsamı için oluşturulmamış.' };
  }

  const headers = EDIT_TEMPLATES[expectedTipi].headers;
  if (!headersMatch(allRows[1], headers)) {
    return {
      ok: false,
      error: `Başlık satırı şablonla eşleşmiyor. Beklenen: ${headers.join(' · ')}`,
    };
  }

  const dataRows = allRows.slice(2).filter((r) => !isRowEmpty(r));
  const rows = [];
  const errors = [];

  dataRows.forEach((row, idx) => {
    const excelRow = idx + 3;
    const _id = cellStr(row[0]);
    if (!_id) {
      errors.push({ row: excelRow, message: '_id boş olamaz.' });
      return;
    }
    if (!/^[a-fA-F0-9]{24}$/.test(_id)) {
      errors.push({ row: excelRow, message: 'Geçersiz _id formatı.' });
      return;
    }

    if (expectedTipi === 'parca') {
      const kodR = parseKod(row[1]);
      const adR = parseAd(row[2]);
      const boolR = parseBoolTrExcel(row[3]);
      const fiyatR = parseFiyat(row[4]);
      if (!kodR.ok) errors.push({ row: excelRow, message: kodR.error });
      if (!adR.ok) errors.push({ row: excelRow, message: adR.error });
      if (!boolR.ok) errors.push({ row: excelRow, message: boolR.error });
      if (!fiyatR.ok) errors.push({ row: excelRow, message: fiyatR.error });
      if (kodR.ok && adR.ok && boolR.ok && fiyatR.ok) {
        rows.push({
          _id,
          icerikKodu: kodR.value,
          icerikAdi: adR.value,
          degisimParcaGarantisi: boolR.value,
          fiyat: fiyatR.value,
        });
      }
      return;
    }

    if (expectedTipi === 'urun') {
      const kodR = parseKod(row[1]);
      const adR = parseAd(row[2]);
      const acR = parseAciklama(row[3]);
      if (!kodR.ok) errors.push({ row: excelRow, message: kodR.error });
      if (!adR.ok) errors.push({ row: excelRow, message: adR.error });
      if (!acR.ok) errors.push({ row: excelRow, message: acR.error });
      if (kodR.ok && adR.ok && acR.ok) {
        rows.push({
          _id,
          icerikKodu: kodR.value,
          icerikAdi: adR.value,
          aciklama: acR.value,
        });
      }
      return;
    }

    const kodR = parseKod(row[1]);
    const adR = parseAd(row[2]);
    const fiyatDahiliR = parseFiyat(row[3]);
    const fiyatHariciR = parseFiyat(row[4]);
    const acR = parseAciklama(row[5]);
    if (!kodR.ok) errors.push({ row: excelRow, message: kodR.error });
    if (!adR.ok) errors.push({ row: excelRow, message: adR.error });
    if (!fiyatDahiliR.ok) errors.push({ row: excelRow, message: fiyatDahiliR.error });
    if (!fiyatHariciR.ok) errors.push({ row: excelRow, message: fiyatHariciR.error });
    if (!acR.ok) errors.push({ row: excelRow, message: acR.error });
    if (kodR.ok && adR.ok && fiyatDahiliR.ok && fiyatHariciR.ok && acR.ok) {
      rows.push({
        _id,
        icerikKodu: kodR.value,
        icerikAdi: adR.value,
        fiyatGarantiDahili: fiyatDahiliR.value,
        fiyatGarantiHarici: fiyatHariciR.value,
        aciklama: acR.value,
      });
    }
  });

  if (errors.length > 0) {
    return { ok: false, error: 'Dosyada satır doğrulama hataları var.', parseErrors: errors };
  }
  if (rows.length === 0) {
    return { ok: false, error: 'Güncellenecek veri satırı yok.' };
  }

  return {
    ok: true,
    icerikTipi: expectedTipi,
    scope: expectedScope,
    templateVersion: TEMPLATE_VERSION,
    rows,
  };
};

/**
 * @param {IcerikEditTipi} icerikTipi
 * @param {IcerikEditScope} scope
 */
export const buildExportFilename = (icerikTipi, scope) => {
  const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const scopePart =
    scope.type === 'kategori' ? `kat-${scope.kategoriId.slice(-6)}` : `urun-${scope.urunIcerikId.slice(-6)}`;
  return `icerik-duzenle-${icerikTipi}-${scopePart}-${stamp}.xlsx`;
};
